import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Stack, Typography } from '@mui/material';
import { GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { useCreateClusterMutation, useGetClustersQuery, useGetEmrsQuery, useGetProjectQuery } from '~/api/v2/hooks';
import { Table, Tooltip } from '~/components';
import { TableFilters } from '~/components/Table/components/filters/types';
import { ProjectActionTypeEnum, useProjectActionState } from '~/hooks';
import { DocumentIcon } from '~/icons';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { getPriviligies } from '~/utils';
import { CreationDisabledChip, TableActionButtons, useFiltersClusterConfig } from '.';
import { columns } from './ClusterTableConfig';
import { createBlockContainerStyles, createBlockStyles } from './styles';
import { ClusterFilters, ClusterFiltersType } from './types';

const EmrClusterBlock = () => {
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const dispatch = useAppDispatch();
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);
  const { data: emrData } = useGetEmrsQuery({ projectId });
  const emrId = emrData?.id || 0;
  const [filters, setFilters] = useState<ClusterFilters>({
    [ClusterFiltersType.ID]: [],
    [ClusterFiltersType.STATUS]: [],
    [ClusterFiltersType.CREATED_ON]: [],
    [ClusterFiltersType.CREATED_BY]: [],
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortModel, setSortModel] = useState<GridSortItem | null>(null);

  const getSortValue = () => {
    switch (sortModel?.field) {
      case 'aws_cluster_id':
        return 'id';
      case 'creator':
        return 'created_by';
      case 'elapsed':
        return;
      default:
        return sortModel?.field;
    }
  };

  const {
    data: response,
    isFetching,
    refetch,
  } = useGetClustersQuery({
    projectId,
    emrId,
    sort: getSortValue(),
    sort_elapsed: sortModel?.field === 'elapsed',
    order: sortModel?.sort,
    search: searchQuery,
    statusList: filters[ClusterFiltersType.STATUS].map((filter) => filter.id),
    createdOn: filters[ClusterFiltersType.CREATED_ON].map((filter) => filter.id),
    createdBy: filters[ClusterFiltersType.CREATED_BY].map((filter) => filter.id) || [],
    clusterId: filters[ClusterFiltersType.ID].map((filter) => filter.id),
  });

  const [, { isLoading }] = useCreateClusterMutation({
    fixedCacheKey: `create-cluster-emr-${emrId}`,
  });
  const { data: project } = useGetProjectQuery({ projectId });

  const inProgress = isLoading || isFetching;

  const { isDisabledAction: isClusterCreationDisabled, tooltipMessage: clusterCreationMessage } = useProjectActionState(
    {
      actionType: ProjectActionTypeEnum.EMR_CLUSTER,
    },
  );

  const userProjectRole = project?.members?.find(({ id }) => id === userId)?.role_id;
  const userPriviligies = getPriviligies(userProjectRole) || getPriviligies(userApplicationRole);

  const filterOptions = useFiltersClusterConfig({ emrId });

  const shouldShowTable = inProgress || response?.data?.length || Object.values(filters).flat().length || searchQuery;

  return (
    <Box sx={createBlockContainerStyles}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5">EMR Clusters</Typography>
        {!!response?.data?.length && (
          <TableActionButtons
            onRefreshButtonClick={refetch}
            onCreateButtonClick={() => {
              dispatch(
                openModal({
                  type: ModalTypesEnum.CREATE_EMR_CLUSTER,
                  payload: {
                    projectName: project?.name || '',
                    emrId,
                  },
                }),
              );
            }}
          />
        )}
      </Stack>
      {shouldShowTable ? (
        <Table
          filterOptions={filterOptions}
          hideFooter
          filterMode="server"
          sortingMode="server"
          paginationMode="server"
          onSortChange={(sortModel: GridSortModel) => {
            setSortModel(sortModel[0] || null);
          }}
          onSearchChange={(query: string) => {
            setSearchQuery(query);
          }}
          onFiltersChange={(filters: TableFilters) => {
            setFilters(filters as ClusterFilters);
          }}
          rows={response?.data || []}
          rowCount={response?.pagination.total || 0}
          columns={columns}
          loading={inProgress}
        />
      ) : (
        <Box sx={createBlockStyles}>
          <DocumentIcon fill="#0000004D" />
          <Typography variant="body1" color="#0000004D">
            No Clusters to display
          </Typography>
          {userPriviligies?.studioIntegrationTab.canEdit && (
            <Tooltip disableHoverListener={!isClusterCreationDisabled} title={clusterCreationMessage}>
              <span>
                <CustomButton
                  disabled={isClusterCreationDisabled}
                  label="Create Cluster"
                  onClick={() => {
                    dispatch(
                      openModal({
                        type: ModalTypesEnum.CREATE_EMR_CLUSTER,
                        payload: {
                          projectName: project?.name || '',
                          emrId,
                        },
                      }),
                    );
                  }}
                />
              </span>
            </Tooltip>
          )}
          {isClusterCreationDisabled && (
            <CreationDisabledChip title="Cluster creation disabled: " description={clusterCreationMessage} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmrClusterBlock;
